import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTasks,
  FaFileInvoice,
  FaChartBar,
  FaChevronDown,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader } from "./Card";
import api from "./api/api";

function DataTable({ title, rows }) {
  const [filters, setFilters] = useState({});
  const [visibleFilters, setVisibleFilters] = useState({});

  if (!rows || rows.length === 0) {
    return (
      <section className="bg-white p-3 rounded-md shadow-sm">
        <h2 className="text-sm font-semibold mb-2">{title}</h2>
        <p className="text-gray-500 text-xs italic">No data found.</p>
      </section>
    );
  }

  const headers = Object.keys(rows[0]);
  const filteredRows = rows.filter((row) =>
    headers.every((h) =>
      (row[h] ?? "")
        .toString()
        .toLowerCase()
        .includes((filters[h] ?? "").toLowerCase())
    )
  );

  const toggleFilter = (header) => {
    setVisibleFilters((prev) => ({ ...prev, [header]: !prev[header] }));
  };
  const formatHeader = (header) => {
    const formatted = header
      .trim();

    return formatted
      .trim();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <section className="bg-white p-3 rounded-md shadow-sm">
      <h2 className="text-sm font-semibold mb-2">{title}</h2>
      <div className="overflow-auto max-h-[300px] border rounded">
        <table className="w-full min-w-[500px] text-xs border-collapse">
          <thead>
            <tr className="bg-gray-200 text-gray-700">
              {headers.map((h) => (
                <th
                  key={h}
                  className="px-2 py-1 text-left font-medium border-b border-gray-300 sticky top-0 bg-gray-200 z-10"
                >
                  <div className="flex flex-col">
                    <div className="flex items-center gap-1">
                      <span>{formatHeader(h)}</span>
                      <button
                        onClick={() => toggleFilter(h)}
                        className="text-[10px] text-gray-600 hover:text-black"
                        title="Toggle filter"
                      >
                        🔍
                      </button>
                    </div>
                    {visibleFilters[h] && (
                      <input
                        type="text"
                        placeholder="Filter"
                        value={filters[h] || ""}
                        onChange={(e) =>
                          setFilters((prev) => ({ ...prev, [h]: e.target.value }))
                        }
                        className="mt-1 p-0.5 text-[10px] border rounded focus:outline-none focus:ring focus:ring-blue-300"
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                {headers.map((h) => (
                  <td key={h} className="px-2 py-1 border-b border-gray-200 whitespace-nowrap">
                    {h.includes("Date") ? formatDate(row[h]) : row[h] ?? "-"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Menu() {
  const navigate = useNavigate();
  const [menuData, setMenuData] = useState([]);
  const [dashboardCards, setDashboardCards] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [activeMenuType, setActiveMenuType] = useState(null);
  const menuRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        //const user = JSON.parse(localStorage.getItem("user") || "{}");
        const user = JSON.parse(sessionStorage.getItem("user") || "{}");
        const username = user.UsrName;
        if (!username) return;

        const res = await api.getJCTrackingRights(username);

        const menuList =
          res.Table?.filter((item) => item.Rights_View === 1) || [];
        const cards = res.Table1?.sort((a, b) => a.Slno - b.Slno) || [];
        const table = res.Table2 || [];

        setMenuData(menuList);
        setDashboardCards(cards);
        setTableData(table);
      } catch (err) {
        console.error("Error fetching menu data", err);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenuType(null);
        clearTimeout(timerRef.current);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const startAutoCloseTimer = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setActiveMenuType(null);
    }, 100);
  };

  const cancelAutoCloseTimer = () => {
    clearTimeout(timerRef.current);
  };

  const menuRoutes = {
    "Job Card Tracker": "/search",
    "Invoice Mapping": "/InvoiceMapping",
    "Upload Pdf Receipt": "/UploadPage",
  };

  const cardColors = [
    "from-blue-500 to-blue-700",
    "from-green-500 to-green-700",
    "from-purple-500 to-purple-700",
    "from-orange-500 to-orange-700",
    "from-teal-500 to-teal-700",
    "from-red-500 to-red-700",
    "from-indigo-500 to-indigo-700",
  ];

  const groupedMenu = menuData.reduce((acc, item) => {
    if (!acc[item.MenuType]) acc[item.MenuType] = [];
    acc[item.MenuType].push(item);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      <nav className="bg-gray-200 shadow-sm border-b border-gray-300">
        <div className="flex flex-wrap p-1" ref={menuRef}>
          {Object.entries(groupedMenu).map(([type, items]) => (
            <div
              key={type}
              className="relative group mr-2"
              onMouseEnter={() => {
                setActiveMenuType(type);
                cancelAutoCloseTimer();
              }}
              onMouseLeave={startAutoCloseTimer}
            >
              <button
                onClick={() =>
                  setActiveMenuType((prev) => (prev === type ? null : type))
                }
                className={`flex items-center gap-1 font-medium text-gray-700 hover:bg-gray-300 focus:outline-none transition-colors duration-200 text-sm px-2 py-1 whitespace-nowrap
                   ${activeMenuType === type ? "bg-gray-300 text-black" : ""}`}
              >
                {type}
                <FaChevronDown
                  className={`transition-transform duration-300 text-xs ${activeMenuType === type ? "rotate-180" : ""
                    }`}
                />
              </button>

              <AnimatePresence>
                {activeMenuType === type && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 mt-0 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-[160px] py-1"
                  >
                    {items.map((menu) => (
                      <button
                        key={menu.MenuName}
                        onClick={() => {
                          navigate(menuRoutes[menu.MenuName] || "/");
                          setActiveMenuType(null);
                        }}
                        className="w-full flex items-center px-3 py-2 text-left text-gray-700 hover:bg-gray-100 transition-colors duration-200 text-sm"
                      >
                        <span>{menu.MenuName}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </nav>

      <main className="p-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
          📊 Dashboard Overview
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {dashboardCards.map((card, index) => (
            <Card
              key={card.Slno}
              className={`bg-gradient-to-br ${cardColors[index % cardColors.length]
                } text-white rounded-xl shadow-lg transform transition-transform duration-300 hover:scale-[1.03] overflow-hidden p-6`}
            >
              <CardHeader
                title={card.CardName}
                subtitle="Overview stats"
                className="text-white mb-2"
              />
              <CardContent>
                <div className="text-2xl font-bold mb-1">{card.NoofJc}</div>
                <p className="text-sm opacity-90 mb-2">Total Job Cards</p>
                <hr className="border-t border-white border-opacity-30" />
                <div className="mt-2 text-lg font-medium">
                  {card.TotalQty}
                </div>
                <p className="text-sm opacity-90">Total Quantity</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <DataTable title="Date Wise Job Card Summary" rows={tableData} />
      </main>
    </div>
  );
}

export default Menu;